import type moment from 'moment'
import { rem } from 'polished'
import type { PropsWithChildren } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'

const Container = styled(Clickable)`
  position: relative;
  display: flex;
  height: fit-content;
  align-items: center;
  width: 100%;
  background-color: ${(props) => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.S};
`

const Item = styled.div`
  padding: ${rem(8)} ${rem(16)};
  width: 100%;
  justify-self: stretch;

  &:not(:first-child) {
    border-inline-start: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  }
`

interface BaseProps extends PropsWithChildren<{}> {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
}

interface DatesProps extends BaseProps {
  checkinDate?: moment.Moment
  checkoutDate?: moment.Moment
  title1?: string
  subTitle1?: string
  title2?: string
  subTitle2?: string
  useSingleDate?: boolean
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
}

export function DatesInputTrigger({
  onClick,
  checkinDate,
  checkoutDate,
  title1,
  subTitle1,
  title2,
  subTitle2,
  useSingleDate,
}: DatesProps) {
  const intl = useIntl()

  const defaultAnytime = intl.formatMessage({
    id: '-3-qfYK_TsezibDc4PV7',
    defaultMessage: 'Anytime',
    description: 'Default subtitle when no dates are selected in date input trigger',
  })

  const resolvedTitle1 = title1 ?? intl.formatMessage({
    id: 'hOs6ZHKG3LM0G0zEVrht',
    defaultMessage: 'Check in',
    description: 'Default label for check-in date in date input trigger',
  })

  const resolvedSubTitle1 = subTitle1 ?? defaultAnytime

  const resolvedTitle2 = title2 ?? intl.formatMessage({
    id: 'B_A6o1p9ctouO72xDbBj',
    defaultMessage: 'Check out',
    description: 'Default label for check-out date in date input trigger',
  })

  const resolvedSubTitle2 = subTitle2 ?? defaultAnytime
  return (
    <Container onClick={onClick}>
      <Item>
        <Caption variant="large" weight="bold">
          {resolvedTitle1}
        </Caption>
        <BodyText
          variant="medium"
          colour={checkinDate ? 'normal-contrast' : 'light-foreground-alt'}
        >
          {checkinDate ? checkinDate.format(DMY_CASUAL_FORMAT) : resolvedSubTitle1}
        </BodyText>
      </Item>
      {!useSingleDate && (
        <Item>
          <Caption variant="large" weight="bold">
            {resolvedTitle2}
          </Caption>
          <BodyText
            variant="medium"
            colour={checkoutDate ? 'normal-contrast' : 'light-foreground-alt'}
          >
            {checkoutDate ? checkoutDate.format(DMY_CASUAL_FORMAT) : resolvedSubTitle2}
          </BodyText>
        </Item>
      )}
    </Container>
  )
}
